/**
 * Feature flag helpers.
 *
 * These are intentionally simple (env driven) so we can safely hide unfinished UX.
 * If we later add server-driven flags, this module becomes the single swap point.
 */

const normalizeEmail = (email?: string | null) => (email ?? '').trim().toLowerCase();

const parseCsv = (value?: string) =>
  (value ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

/**
 * Coach Compliance tab gating.
 *
 * - ON: local dev (`npm start`), staging, and pre-production / branch deploys where
 *   `REACT_APP_ENVIRONMENT` is not `production` (e.g. `staging` from Vercel "Pre-Production").
 * - OFF: production (`REACT_APP_ENVIRONMENT === 'production'`), except allowlist / force flag.
 *
 * Uses `REACT_APP_ENVIRONMENT` only — not `NODE_ENV` or Sentry env — so preview builds
 * (production webpack + `REACT_APP_ENVIRONMENT=staging`) behave like staging.
 */
export function isCoachComplianceEnabledForUser(email?: string | null): boolean {
  const appEnv = (process.env.REACT_APP_ENVIRONMENT ?? '').trim().toLowerCase();
  const explicitlyEnabled = (process.env.REACT_APP_ENABLE_COACH_COMPLIANCE ?? '').trim() === 'true';

  if (explicitlyEnabled) return true;

  if (appEnv === 'production') {
    const allowlist = parseCsv(process.env.REACT_APP_COACH_COMPLIANCE_EMAIL_ALLOWLIST);
    if (allowlist.length === 0) return false;
    const e = normalizeEmail(email);
    return e.length > 0 && allowlist.map((x) => x.toLowerCase()).includes(e);
  }

  // staging, unset, or any non-production label — includes Vercel pre-production with REACT_APP_ENVIRONMENT=staging
  return true;
}

/**
 * Rower training dashboard (metrics, heatmap, recent Peach/Concept2 sessions).
 *
 * Phase 1 (GLY-398) defaults OFF so the rower home is recruiting-focused.
 * Set REACT_APP_ENABLE_ROWER_TRAINING_DASHBOARD=true to restore the full training home.
 * Concept2 connect/import stays available via Integrations regardless of this flag.
 */
export function isRowerTrainingDashboardEnabled(): boolean {
  return (process.env.REACT_APP_ENABLE_ROWER_TRAINING_DASHBOARD ?? '').trim() === 'true';
}

/**
 * Rower Peach integration surfaces (hub card, Peach detail route, Sessions nav).
 *
 * Phase 1 defaults OFF. Concept2 stays available.
 * Set REACT_APP_ENABLE_ROWER_PEACH_INTEGRATION=true to restore Peach/Sessions.
 */
export function isRowerPeachIntegrationEnabled(): boolean {
  return (process.env.REACT_APP_ENABLE_ROWER_PEACH_INTEGRATION ?? '').trim() === 'true';
}

/**
 * Rower sharing surfaces (Teams, Integrations, Invitations nav, Concept2 prompts/cards, team sharing settings).
 *
 * Phase 1 (GLY-398) defaults OFF so the rower experience stays recruitment-focused.
 * Set REACT_APP_ENABLE_ROWER_SHARING_FEATURES=true for the full sharing rollout.
 */
export function isRowerSharingFeaturesEnabled(): boolean {
  return (process.env.REACT_APP_ENABLE_ROWER_SHARING_FEATURES ?? '').trim() === 'true';
}
